import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const searchTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isSQLite = stack.database === 'sqlite';
  const isPostgres =
    stack.database === 'postgres' || stack.database === 'mysql';

  const scope =
    (featureConfig?.options['search-scope'] as string | undefined) ?? 'basic';
  const hasFacets = scope === 'faceted' || scope === 'advanced';
  const hasTypeahead = scope !== 'basic';

  return {
    pages: [
      {
        id: 'search',
        path: '/search',
        label: 'Search',
        description:
          'Full-text search results with filters, sorting, and pagination',
        authRequired: false,
      },
      ...(hasFacets
        ? [
            {
              id: 'search-advanced',
              path: '/search/advanced',
              label: 'Advanced search',
              description:
                'Multi-field query builder with date ranges and facet filters',
              authRequired: false,
            },
          ]
        : []),
    ],
    uxFlows: [
      {
        id: 'full-text-search',
        title: 'User performs a full-text search',
        steps: [
          'User types a query in the global search bar (keyboard shortcut: ⌘K)',
          'Client debounces input by 300 ms, then calls GET /api/search?q=...',
          isPostgres
            ? 'Server executes a tsvector full-text query: WHERE search_vector @@ plainto_tsquery($1), ordered by ts_rank'
            : isSQLite
              ? 'Server runs: SELECT * FROM search_fts WHERE search_fts MATCH ? ORDER BY rank'
              : 'Server runs MongoDB $text search: { $text: { $search: query } }, sorted by { score: { $meta: "textScore" } }',
          'Results are returned with matched field snippets (ts_headline in Postgres, snippet() in SQLite)',
          'User clicks a result and is navigated to the corresponding entity',
        ],
      },
      ...(hasTypeahead
        ? [
            {
              id: 'typeahead',
              title: 'User gets typeahead suggestions',
              steps: [
                'User starts typing in the search input (min 2 characters)',
                'Client debounces by 150 ms, calls GET /api/search/suggest?q=...',
                isPostgres
                  ? 'Server queries using ILIKE or prefix tsvector match for fast suggestions'
                  : isSQLite
                    ? 'Server runs FTS5 prefix match: WHERE search_fts MATCH \'title: "query*"\'  '
                    : 'Server uses MongoDB autocomplete index or $regex prefix query',
                'Up to 8 suggestions returned with entity type and title',
                'User selects a suggestion → navigated directly to that entity',
              ],
            },
          ]
        : []),
      ...(hasFacets
        ? [
            {
              id: 'faceted-filter',
              title: 'User filters results by facet',
              steps: [
                'Search results page renders facet counts in sidebar (e.g. Type, Date, Author)',
                'User clicks a facet value — filter is added to URL query params',
                'GET /api/search?q=...&filters[type]=post&filters[author]=alice re-fetches',
                isPostgres
                  ? 'Server adds WHERE clauses for each active filter alongside tsvector match'
                  : isMongo
                    ? 'Server combines $text match with additional $match stages in aggregation pipeline'
                    : 'Server adds AND conditions to the FTS5 query',
                'Facet counts update to reflect intersection with active filters',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      {
        id: 'search',
        method: 'GET',
        path: '/api/search',
        description:
          'Full-text search across indexed entities with optional filters and pagination',
        response:
          '{ results: SearchResult[], total: number, facets?: FacetGroup[], nextCursor?: string }',
      },
      ...(hasTypeahead
        ? [
            {
              id: 'suggest',
              method: 'GET' as const,
              path: '/api/search/suggest',
              description:
                'Typeahead suggestions — fast prefix match, max 8 results',
              response:
                '{ suggestions: { label: string, type: string, url: string }[] }',
            },
          ]
        : []),
      {
        id: 'index-entity',
        method: 'POST',
        path: '/api/search/index',
        description:
          'Internal endpoint — re-index a single entity after create/update (called from service layer)',
        requestBody: '{ entityType, entityId }',
        response: '{ ok: true }',
      },
    ],
    dbTables: isPostgres
      ? [
          {
            id: 'search_index',
            name: '(tsvector column on each searchable table)',
            columns: [],
            notes:
              "Add a generated tsvector column to each searchable table: ALTER TABLE items ADD COLUMN search_vector tsvector GENERATED ALWAYS AS (to_tsvector('english', coalesce(title,'') || ' ' || coalesce(description,''))) STORED; CREATE INDEX idx_items_search ON items USING GIN(search_vector); Update via triggers or on write in the service layer. Use ts_rank for relevance ordering and ts_headline for snippet generation.",
          },
        ]
      : isSQLite
        ? [
            {
              id: 'search_fts',
              name: 'search_fts (FTS5 virtual table)',
              columns: [
                {
                  name: 'rowid',
                  type: 'integer',
                  nullable: false,
                  notes: 'Implicit FTS5 rowid, maps to entity primary key',
                },
                { name: 'entity_type', type: 'text', nullable: false },
                {
                  name: 'entity_id',
                  type: 'text',
                  nullable: false,
                  notes: 'UUID of the source row',
                },
                { name: 'title', type: 'text', nullable: false },
                { name: 'body', type: 'text', nullable: false },
                {
                  name: 'author',
                  type: 'text',
                  nullable: true,
                  notes: 'Denormalized for typeahead filter',
                },
              ],
              notes:
                "CREATE VIRTUAL TABLE search_fts USING fts5(entity_type UNINDEXED, entity_id UNINDEXED, title, body, author, tokenize='porter unicode61'); Populate via AFTER INSERT/UPDATE/DELETE triggers on source tables. Use snippet(search_fts, 3, '<b>', '</b>', '...', 20) for highlighted excerpts.",
            },
          ]
        : [
            {
              id: 'search_index',
              name: '(MongoDB Atlas Search or $text index)',
              columns: [],
              notes:
                'For Atlas: create a search index with { mappings: { dynamic: true } } or explicit field mappings. For self-hosted: db.items.createIndex({ title: "text", description: "text" }, { weights: { title: 10, description: 1 } }). Use $meta: "textScore" for relevance ordering. For autocomplete, use Atlas Search autocomplete analyzer.',
            },
          ],
  };
};
